/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import { useCart } from "../../hooks/useCart";
import Icon from "../ui/Icon";
import styled from "styled-components";
import Flex from "../ui/Flex";
import CartLineRow from "../CartLineRow";
import Button from "../ui/Button";
import { Modal, ModalBody, ModalContent, ModalTitle } from "../ui/Modal";
import PriceBreakdown from "../booking/PriceBreakdown";
import { useCartSummary } from "../../hooks/useCartSummary";
import EmptyView from "../EmptyView";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants/constants";


const CartWrapper = styled(Flex)`
  flex: 1;
  min-height: 0;
`;

const FooterStepButton = styled(Flex)`
  margin-top: 1.5rem;
  border-radius: 0.5rem 0.5rem 0 0;
`;

const EmptyCartWrapper = styled(Flex)`
  width: 100%;
  height: 100%;
`;

const StyledModalBody = styled(ModalBody)`
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 1.5rem;
  padding-block: 2rem;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scroll-padding-block: 4px;
  overscroll-behavior: contain;
`;

const StickyFooter = styled.div`
  flex-shrink: 0;
`;

const CartDrawer = () => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { lines, totalQuantity, setLineQty, deleteLine } = useCart();
  const navigate = useNavigate();

  const cartSummary = useCartSummary();

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate(ROUTES.ORDER_SUMMARY);
  };

  return (
    <>
      <Button variant="transparent" size="icon" icon={<Icon.Cart size={Icon.SIZE.SM}/>} count={totalQuantity || null} onClick={() => setIsCartOpen(true)} />

      <Modal isOpen={isCartOpen} onClose={() => setIsCartOpen(false)}>
        <ModalContent placement="right">
          <StyledModalBody>
            <ModalTitle>Your Order</ModalTitle>
            <CartWrapper $direction="column" $gap="1rem">
              <ScrollArea>
                {lines.length > 0 ?
                  lines.map((line) => (
                    <CartLineRow
                      key={line.clientId}
                      line={line}
                      showThumbnail
                      onQtyChange={setLineQty}
                      onRemove={deleteLine}
                    />
                  ))
                  : (
                    <EmptyCartWrapper $justify="center" $align="center">
                      <EmptyView icon={Icon.Cart} description="your_cart_is_empty" />
                    </EmptyCartWrapper>
                  )}
              </ScrollArea>
              <StickyFooter>
                {lines.length > 0 && (
                  <PriceBreakdown
                    currency={cartSummary.currency}
                    subtotal={cartSummary.subtotal}
                    taxes={cartSummary.taxes}
                    price={cartSummary.total}
                  />
                )}

                <FooterStepButton $direction="column" $gap="0.5rem">
                  {lines.length > 0 && (
                    <Button title="Proceed to checkout" fullWidth onClick={handleCheckout} />
                  )}
                  <Button
                    title="Continue shopping"
                    fullWidth
                    variant="secondary"
                    onClick={() => setIsCartOpen(false)}
                  />
                </FooterStepButton>
              </StickyFooter>
            </CartWrapper>
          </StyledModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CartDrawer;
